import {
  CheckCircleIcon,
  ComputerDesktopIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { animations, viewportConfig } from '../config/animations';
import { useContactForm } from '../hooks/useContactForm';

const Contact = () => {
  const {
    formData,
    isSubmitting,
    isSubmitted,
    handleInputChange,
    handleSubmit,
    resetForm,
  } = useContactForm();

  const { t } = useTranslation();
  // Reset form after submission (same logic as ContactModal)
  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        resetForm();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted, resetForm]);

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: t('contact.contactInfo.phones.title'),
      details: ['(11) 5180-8900'],
      color: 'from-avoprime-primary to-avoprime-secondary',
    },
    {
      icon: EnvelopeIcon,
      title: t('contact.contactInfo.email.title'),
      details: ['comercial@avoprime.com.br'],
      color: 'from-avoprime-light to-green-400',
    },
    {
      icon: MapPinIcon,
      title: t('contact.contactInfo.location.title'),
      details: ['Cristina - MG', 'Serra da Mantiqueira'],
      color: 'from-avoprime-primary to-avoprime-secondary',
    },
    {
      icon: ComputerDesktopIcon,
      title: t('contact.contactInfo.socialMedia.title'),
      details: [
        'LinkedIn: /avoprime',
        'Instagram: @avoprimebr'
      ],
      color: 'from-avoprime-light to-green-400',
      isSocialMedia: true,
    },
  ];


  return (
    <section
      id='contact'
      className='py-8 bg-gradient-to-br from-avoprime-primary/5 to-avoprime-secondary/5'
    >
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <motion.div
          {...animations.fadeInUp}
          whileInView={animations.fadeInUp.animate}
          viewport={viewportConfig}
          className='text-center mb-16'
        >
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-eras font-bold text-avoprime-primary mb-6'>
            {t('contact.title')}
          </h2>
          <p className='text-xl lg:text-2xl font-montserrat text-gray-700 max-w-4xl mx-auto leading-relaxed'>
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className='grid lg:grid-cols-2 gap-12'>
          {/* Contact Information */}
          <motion.div
            variants={animations.containerVariants}
            initial='hidden'
            whileInView='visible'
            viewport={viewportConfig}
            className='space-y-8'
          >
            <h3 className='text-2xl sm:text-3xl font-eras font-bold text-avoprime-primary mb-8 text-center lg:text-left'>
              {t('contact.contactInfo.title')}
            </h3>

            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                variants={animations.itemVariants}
                className='flex flex-col lg:flex-row items-center lg:items-start space-y-4 lg:space-y-0 lg:space-x-4 text-center lg:text-left'
              >
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${info.color} rounded-full flex items-center justify-center flex-shrink-0`}
                >
                  <info.icon className='w-8 h-8 text-white' />
                </div>
                <div className='w-full lg:w-auto'>
                  <h4 className='text-lg sm:text-xl font-eras font-bold text-avoprime-primary mb-2'>
                    {info.title}
                  </h4>
                  {info.isSocialMedia ? (
                    <div className='flex flex-col space-y-3'>
                      <div className='flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3'>
                        <a
                          href='https://www.instagram.com/avoprimebr/?utm_source=ig_web_button_share_sheet'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='w-10 h-10 bg-gradient-to-br from-avoprime-primary to-avoprime-secondary rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:shadow-lg'
                        >
                          <svg className='w-5 h-5 text-white' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'/>
                          </svg>
                        </a>
                        <span className='text-gray-700 font-montserrat text-base sm:text-lg'>@avoprimebr</span>
                      </div>
                      <div className='flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3'>
                        <a
                          href='https://www.linkedin.com/company/avoprime/'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='w-10 h-10 bg-gradient-to-br from-avoprime-primary to-avoprime-secondary rounded-lg flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:shadow-lg'
                        >
                          <svg className='w-5 h-5 text-white' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'/>
                          </svg>
                        </a>
                        <span className='text-gray-700 font-montserrat text-base sm:text-lg'>/avoprime</span>
                      </div>
                    </div>
                  ) : (
                    info.details.map((detail, detailIndex) => (
                      <p
                        key={detailIndex}
                        className='text-gray-700 font-montserrat text-base sm:text-lg'
                      >
                        {detail}
                      </p>
                    ))
                  )}
                </div>
              </motion.div>
            ))}

          </motion.div>

          {/* Contact Form */}
          <motion.div
            {...animations.fadeInRight}
            whileInView={animations.fadeInRight.animate}
            viewport={viewportConfig}
            className='bg-white rounded-3xl p-8 lg:p-12 shadow-2xl border border-gray-100'
          >
            <h3 className='text-2xl sm:text-3xl font-eras font-bold text-avoprime-primary mb-8 text-center lg:text-left'>
              {t('contact.form.title')}
            </h3>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className='text-center py-12'
              >
                <CheckCircleIcon className='w-20 h-20 text-green-500 mx-auto mb-6' />
                <h4 className='text-xl sm:text-2xl font-eras font-bold text-avoprime-primary mb-4'>
                  {t('contact.form.success')}
                </h4>
                <p className='text-gray-600 font-montserrat'>
                  {t('contact.form.successMessage')}
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className='space-y-6 text-center lg:text-left'>
                <div className='grid md:grid-cols-2 gap-6'>
                  <div>
                    <label
                      htmlFor='name'
                      className='block text-xs sm:text-sm font-medium text-gray-700 mb-2 text-center lg:text-left'
                    >
                      {t('contact.form.name')} *
                    </label>
                    <input
                      type='text'
                      id='name'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                      placeholder={t('contact.form.placeholder.name')}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='email'
                      className='block text-xs sm:text-sm font-medium text-gray-700 mb-2 text-center lg:text-left'
                    >
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                      placeholder={t('contact.form.placeholder.email')}
                    />
                  </div>
                </div>

                <div className='grid md:grid-cols-2 gap-6'>
                  <div>
                    <label
                      htmlFor='phone'
                      className='block text-xs sm:text-sm font-medium text-gray-700 mb-2 text-center lg:text-left'
                    >
                      {t('contact.form.phone')}
                    </label>
                    <input
                      type='tel'
                      id='phone'
                      name='phone'
                      value={formData.phone}
                      onChange={handleInputChange}
                      className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                      placeholder={t('contact.form.placeholder.phone')}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='company'
                      className='block text-xs sm:text-sm font-medium text-gray-700 mb-2 text-center lg:text-left'
                    >
                      {t('contact.form.company')}
                    </label>
                    <input
                      type='text'
                      id='company'
                      name='company'
                      value={formData.company}
                      onChange={handleInputChange}
                      className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200'
                      placeholder={t('contact.form.placeholder.company')}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor='message'
                    className='block text-xs sm:text-sm font-medium text-gray-700 mb-2 text-center lg:text-left'
                  >
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    id='message'
                    name='message'
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avoprime-primary focus:border-transparent transition-all duration-200 resize-none'
                    placeholder={t('contact.form.placeholder.message')}
                  />
                </div>

                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='w-full bg-gradient-to-r from-avoprime-primary to-avoprime-secondary text-white font-eras text-sm sm:text-lg px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                >
                  {isSubmitting ? (
                    <div className='flex items-center justify-center space-x-2'>
                      <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                      <span>{t('contact.form.sending')}</span>
                    </div>
                  ) : (
                    t('contact.form.submit')
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
